<template>
	<!-- Misma grilla de 12avos que AccountPlanRow y los headers de la vista:
		1 (Nro) / 2 (Nivel 1) / 2-3 (Nivel 2) / 3-4 (Nivel 3) / [2 (Cuenta X)]
		/ 1 (Orden) / 1 (acciones) — cada campo cae EXACTAMENTE bajo su
		columna. Con catálogo de Cuenta X vacío se reparte igual que los
		headers sin esa columna. -->
	<div class="columns is-vcentered">
		<div class="column py-2 is-1"></div>
		<div class="column py-2 is-2">
			<b-select size="is-small" expanded class="ap-field default_empty"
				id="level_1_select" v-model="level1Proxy" placeholder="Nivel 1" rules="required"
				@input="$emit('level1-change', $event)">
				<option v-for="value in level1Values" :key="value.name" :value="value.name">
					{{ value.name }}
				</option>
			</b-select>
		</div>
		<div :class="['column', 'py-2', hasBalanceConcepts ? 'is-2' : 'is-3']">
			<b-select size="is-small" expanded class="ap-field default_empty"
				id="level_2_select" v-model="level2Proxy" placeholder="Nivel 2"
				:key="level2SelectKey" rules="required">
				<option v-for="value in level2Options" :key="value.id" :value="value.id">
					{{ value.level_2 }}
				</option>
			</b-select>
		</div>
		<div :class="['column', 'py-2', hasBalanceConcepts ? 'is-3' : 'is-4']">
			<b-input size="is-small" expanded class="ap-field" placeholder="Cuenta"
				v-model="level3Proxy" />
		</div>
		<div v-if="hasBalanceConcepts" class="column py-2 is-2">
			<b-select size="is-small" expanded class="ap-field default_empty"
				id="balance_concept_select" v-model="balanceConceptProxy"
				placeholder="Cuenta X">
				<option value="">Sin Cuenta X</option>
				<option v-for="concept in balanceConcepts" :key="concept" :value="concept">
					{{ concept }}
				</option>
			</b-select>
		</div>
		<div class="column py-2 is-1">
			<b-input size="is-small" expanded class="ap-field ap-field--order"
				placeholder="Orden" v-model="orderProxy" />
		</div>
		<!-- px-1: mismo truco que la columna de iconos de la fila — la is-1
			no alcanza para dos botones con el gutter default. -->
		<div class="column py-2 px-1 is-1 ap-actions">
			<button type="button" :class="[submitClass, 'ap-inline-action']"
				@click="$emit('submit')">
				{{ submitLabel }}
			</button>
			<button v-if="cancellable" type="button" class="ap-cancel"
				aria-label="Cancelar edición" @click="$emit('cancel')">
				✕
			</button>
		</div>
	</div>
</template>

<script>
export default {
	name: "AccountPlanInlineForm",
	props: {
		level1Values: {
			type: Array,
			default: () => [],
		},
		level2Options: {
			type: Array,
			default: () => [],
		},
		level1: {
			type: String,
			default: "",
		},
		level2: {
			type: [String, Number],
			default: "",
		},
		level3: {
			type: String,
			default: "",
		},
		order: {
			type: [String, Number],
			default: "",
		},
		// "Cuenta X" de los KPIs de Balance. Con catálogo vacío el select no
		// se muestra y la grilla se reparte como los headers sin esa columna.
		balanceConcepts: {
			type: Array,
			default: () => [],
		},
		balanceConcept: {
			type: String,
			default: "",
		},
		submitLabel: {
			type: String,
			default: "OK",
		},
		submitClass: {
			type: String,
			default: "theme_btn_dark",
		},
		// Muestra el botón ✕ que emite `cancel` (formulario de edición; el de
		// alta no se cancela, se deja vacío).
		cancellable: {
			type: Boolean,
			default: false,
		},
		// El formulario de edición fuerza el remount del select de Nivel 2
		// cuando cambian sus opciones (:key original); el de alta no lo hacía.
		level2Remount: {
			type: Boolean,
			default: false,
		},
	},
	computed: {
		level1Proxy: {
			get() {
				return this.level1;
			},
			set(value) {
				this.$emit("update:level1", value);
			},
		},
		level2Proxy: {
			get() {
				return this.level2;
			},
			set(value) {
				this.$emit("update:level2", value);
			},
		},
		level3Proxy: {
			get() {
				return this.level3;
			},
			set(value) {
				this.$emit("update:level3", value);
			},
		},
		orderProxy: {
			get() {
				return this.order;
			},
			set(value) {
				this.$emit("update:order", value);
			},
		},
		balanceConceptProxy: {
			get() {
				return this.balanceConcept;
			},
			set(value) {
				this.$emit("update:balanceConcept", value);
			},
		},
		hasBalanceConcepts() {
			return this.balanceConcepts.length > 0;
		},
		level2SelectKey() {
			// Una key constante equivale a no tener key para un nodo único.
			return this.level2Remount ? this.level2Options.length : "level_2_select";
		},
	},
};
</script>
<style scoped lang="scss">
@use "./account_plan_buttons" as *;

// ── Inputs / selects ────────────────────────────────────────────────────
// Sin márgenes propios: el gutter de la grilla ya separa; cualquier offset
// extra desalinea los campos de sus columnas.
.ap-field {
	:deep(.input),
	:deep(.select select) {
		border-radius: var(--ap-radius-sm);
		border-color: var(--ap-line-strong);
		font-family: var(--ap-font);
		transition: border-color 0.18s var(--ap-ease), box-shadow 0.18s var(--ap-ease);

		&:focus,
		&:focus-within {
			border-color: var(--ap-teal);
			box-shadow: 0 0 0 3px var(--ap-teal-wash);
		}
	}

	:deep(.select select) {
		font-weight: 600;
	}

	&--order :deep(.input) {
		text-align: center;
		font-variant-numeric: tabular-nums;
	}
}

// ── Acciones (misma columna que los iconos de la fila: 28px + gap) ──────
.ap-actions {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 0.25rem;
}

.ap-inline-action {
	height: 28px;
	padding: 0 10px;
	font-size: 13px;
}

.ap-cancel {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: 28px;
	height: 28px;
	padding: 0;
	background: transparent;
	border: 1px solid var(--ap-line);
	border-radius: var(--ap-radius-sm);
	color: var(--ap-slate);
	font-size: 12px;
	line-height: 1;
	cursor: pointer;
	transition: color 0.18s var(--ap-ease), border-color 0.18s var(--ap-ease);

	&:hover {
		color: var(--ap-ink);
		border-color: var(--ap-line-strong);
	}

	&:focus-visible {
		outline: 2px solid var(--ap-teal);
		outline-offset: 2px;
	}
}
</style>
